import type { components } from '#shopware';
import { Build } from '@stencil/core';
import {
  readProduct,
  readProductCrossSelling,
  readProductDetail,
  readProductListing,
  readProductStream
} from '~service/shopware/endpoints';

const cache: Map<string, any> = new Map();

if (Build.isBrowser) {
  (TYPO3.xhrCache || []).forEach(entry => addToCache(normalizeUrl(entry.url), entry.data));
} else if (Build.isServer) {
  const settings = JSON.parse(document.querySelector('body').getAttribute('data-nlx-ssr-settings')) || {};
  (settings.xhrCache || []).forEach(entry => addToCache(normalizeUrl(entry.url), entry.data));
}

function extractConfig() {
  if (Build.isBrowser) {
    return TYPO3.settings || {shopware: {}};
  } else if (Build.isServer) {
    return JSON.parse(document.querySelector('body').getAttribute('data-nlx-ssr-settings')) || {};
  }
  return {shopware: {}};
}

let {shopware} = extractConfig();

function normalizeUrl(url: string): string {
  const urlObject = document.createElement('a');
  urlObject.href = url.replace('%20', '+');
  return urlObject.href;
}

function addToCache(identifier: string, response: any) {
  if (Build.isDev) {
    console.log(`Add ${identifier} to cache with data:`, response);
  }
  cache.set(identifier, JSON.stringify(response));
  return response;
}

function readFromCache(identifier: string): any {
  return JSON.parse(cache.get(identifier));
}

// https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest#converting_a_digest_to_a_hex_string
async function sha256(payload: any): Promise<string> {
  const hashBuffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(payload));
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join(''); // convert bytes to hex string
}

async function createCacheIdentifier(url: string, data: any): Promise<string> {
  const normalizedUrl = normalizeUrl(url);
  const hash = await sha256(JSON.stringify(data));
  return `${normalizedUrl}-${hash}`;
}

async function post(url: string, data: any, useCache = true): Promise<any> {
  const normalizedUrl = normalizeUrl(url);
  const cacheIdentifier = await createCacheIdentifier(normalizedUrl, data);
  if (useCache && cache.has(cacheIdentifier)) {
    return readFromCache(cacheIdentifier);
  } else if (useCache && cache.has(normalizedUrl)) {
    // all cache entries should have a unique identifier and not the url expect the initial loading
    const result = readFromCache(normalizedUrl);
    cache.delete(normalizedUrl);
    if (result) {
      return result;
    }
  } else {
    return await fetch(normalizedUrl, {
      method: 'POST',
      cache: 'no-cache',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'sw-include-seo-urls': 'true',
        'sw-language-iso-code': shopware.isoCode || 'de-DE', // todo: settings are currently not available in the server context
      },
      body: JSON.stringify(data),
    })
      .then(response => response.json())
      .then(response => useCache ? addToCache(cacheIdentifier, response) : response);
  }
}

const ProductListingIncludes = {
  'product_listing': [
    'aggregations',
    'currentFilters',
    'category',
    'elements',
    'limit',
    'page',
    'total'
  ],
  'category': [
    'id',
    'name',
    'isNew',
    'seoUrls',
    'metaDescription',
    'metaTitle',
    'keywords'
  ],
  'product': [
    'id',
    'name',
    'isNew',
    'seoUrls',
    'uvexInfos',
    'groupedVariants',
    'cover',
    'translated'
  ],
  'product_media': [
    'id',
    'media',
    'position'
  ],
  'seo_url': [
    'id',
    'url',
    'isCanonical'
  ],
  'media': [
    'id',
    'alt',
    'title',
    'translated',
    'metaData',
    'thumbnails',
    'url'
  ],
  'media_thumbnail': [
    'id',
    'height',
    'width',
    'url'
  ]
};

export async function getProductList(
  categoryId: string,
  body: components['schemas']['ProductListingCriteria'] & components['schemas']['ProductListingFlags']
): Promise<components['schemas']['ProductListingResult']> {
  // Keep this settings in sync with Netlogix\Nxshopware\Api\StoreApiClient::getProductListing
  return await post(readProductListing(categoryId), {
    ...body, ...{
      'category-based-filters': true,
      'limit': 24,
      'includes': ProductListingIncludes,
      'associations': {
        'media': {},
      }
    }
  });
}

export async function getProduct(
  body: components['schemas']['Criteria']
): Promise<components['schemas']['ProductResponse']> {
  return await post(readProduct(), body);
}

export async function getProductDetail(
  productId: string,
  body: components['schemas']['Criteria']
): Promise<components['schemas']['ProductDetailResponse']> {
  // Keep this settings in sync with Netlogix\Nxshopware\Api\StoreApiClient::getProduct
  return await post(readProductDetail(productId), {
    ...body, ...{
      'includes': {
        'product': [
          'id',
          'ean',
          'assets',
          'configurations',
          'cover',
          'description',
          'groupedVariants',
          'isNew',
          'keywords',
          'manufacturer',
          'media',
          'metaDescription',
          'metaTitle',
          'name',
          'optionIds',
          'packUnit',
          'productNumber',
          'properties',
          'seoUrls',
          'translated',
          'uvexInfos',
          'uvexTabs',
        ],
        'manufacturer': [
          'id',
          'name'
        ],
        'product_media': [
          'id',
          'media',
          'position'
        ],
        'seo_url': [
          'id',
          'url',
          'isCanonical'
        ],
        'media': [
          'id',
          'alt',
          'title',
          'translated',
          'metaData',
          'thumbnails',
          'url'
        ],
        'media_thumbnail': [
          'id',
          'height',
          'width',
          'url'
        ],
        'property_group': [
          'id',
          'name',
          'description',
          'displayType',
          'options',
          'position',
          'sortingType',
          'translated',
          'visibleOnProductDetailPage',
        ],
        'property_group_option': [
          'id',
          'group',
          'groupId',
          'media',
          'displayType',
          'name',
          'position',
          'translated',
        ]
      },
      associations: {
        assets: {},
        options: {
          associations: {
            group: {}
          }
        },
        media: {},
        properties: {
          associations: {
            group: {}
          }
        },
        seoUrls: {},
      }
    }
  });
}

export async function getProductCrossSelling(
  productId: string,
  type: 'accessories' | 'similarArticles'
): Promise<components['schemas']['CrossSellingElement']> {
   const result = await post(readProductCrossSelling(productId), {
    ...{
      includes: ProductListingIncludes,
      associations: {
        media: {},
      }
    }
  });

  return result.find((item: components['schemas']['CrossSellingElement']) => item.crossSelling?.extensions?.uvexConfiguration?.identifier === type);
}

export async function getProductStream(
  streamId: string,
  body: components['schemas']['ProductListingCriteria'] & components['schemas']['ProductListingFlags']
): Promise<components['schemas']['ProductListingResult']> {
  return await post(readProductStream(streamId), {
    ...body, ...{
      includes: ProductListingIncludes,
      associations: {
        media: {},
      }
    }
  });
}
